/**
 * In-product notifications (§10 F12): exact unread count (capped for display), grouped by recency, mark one or
 * all read, clear read ones. A destination in a notification is untrusted: it must be a same-origin, path-
 * relative address that matches a real in-product route, or it isn't a link at all.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { ROUTES } from '@/routes/table';
import { time as fmtTime, date as fmtDate } from '@/lib/format';
import { cn } from '@/lib/cn';
import { writeDelete, writeUpdate } from '@/lib/write';
import { useToast } from '@/components/ui/Toast';

const PATTERNS = ROUTES.filter((r) => r.area !== 'public').map((r) => new RegExp(`^${r.path.replace(/:\w+/g, '[^/]+')}$`));
export function safeDestination(url: string | null | undefined): string | null {
  if (!url || !url.startsWith('/') || url.startsWith('//') || url.includes('\\')) return null;
  const path = url.split(/[?#]/)[0]!;
  return PATTERNS.some((re) => re.test(path)) ? url : null;
}

interface Note { id: string; category: string; title: string; body: string; action_url: string | null; read_at: string | null; created_at: string }
const LABEL: Record<string, string> = { checkin: 'Check-in', checkout: 'Check-out', payment: 'Payment', message: 'Message', change: 'Change', mention: 'Mention', reservation: 'Reservation', general: 'General' };

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['notifications'],
    refetchInterval: 30_000,
    queryFn: async () => {
      const [list, unread] = await Promise.all([
        supabase.from('notifications').select('id, category, title, body, action_url, read_at, created_at').order('created_at', { ascending: false }).limit(50),
        supabase.from('notifications').select('id', { count: 'exact', head: true }).is('read_at', null),
      ]);
      if (list.error) throw list.error;
      return { list: (list.data ?? []) as Note[], unread: unread.count ?? 0 };
    },
  });
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const unread = q.data?.unread ?? 0;
  const refresh = () => void qc.invalidateQueries({ queryKey: ['notifications'] });
  const toast = useToast();
  const markRead = async (ids: string[]) => {
    if (!ids.length) return;
    const r = await writeUpdate(supabase, 'notifications', { read_at: new Date().toISOString() }, { id: ids }, { expected: ids.length });
    if (!r.ok) toast.error(r.message, 'write-not-saved');
  };
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86_400_000).toDateString();
  const groups: Array<[string, Note[]]> = [['Today', []], ['Yesterday', []], ['Earlier', []]];
  for (const n of q.data?.list ?? []) {
    const d = new Date(n.created_at).toDateString();
    groups[d === today ? 0 : d === yesterday ? 1 : 2]![1].push(n);
  }
  return (
    <div className="relative" ref={ref}>
      <button className="relative inline-flex size-9 items-center justify-center rounded-control text-ink-2 hover:bg-surface-2 hover:text-ink" aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open} onClick={() => setOpen((o) => !o)} data-testid="notification-bell">
        <Bell className="size-4" aria-hidden />
        {unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-4 rounded-full bg-st-danger px-1 text-center text-[10px] leading-4 font-semibold text-on-danger" data-testid="notification-count">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 z-popover mt-2 w-[min(22rem,calc(100vw-2rem))] rounded-panel bg-popover elevate ring-1 ring-line" role="dialog" aria-label="Notifications" data-testid="notification-panel">
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <span className="text-sm font-semibold text-ink">Notifications</span>
            <div className="flex gap-2 text-[12px]">
              {unread > 0 && <button className="text-ink-2 underline decoration-brand underline-offset-2 hover:text-ink" onClick={async () => { await markRead((q.data?.list ?? []).filter((n) => !n.read_at).map((n) => n.id)); refresh(); }}>Mark all read</button>}
              {(q.data?.list ?? []).some((n) => n.read_at) && <button className="text-ink-2 underline decoration-brand underline-offset-2 hover:text-ink" onClick={async () => { const ids = (q.data?.list ?? []).filter((n) => n.read_at).map((n) => n.id); const r = await writeDelete(supabase, 'notifications', { id: ids }, { expected: ids.length }); if (!r.ok) toast.error(r.message, 'write-not-saved'); refresh(); }}>Clear read</button>}
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {!q.data?.list.length ? <p className="px-3 py-6 text-center text-sm text-muted">You're all caught up.</p> : groups.filter(([, ns]) => ns.length).map(([label, ns]) => (
              <section key={label}>
                <h3 className="bg-surface-2 px-3 py-1 text-[11px] font-semibold tracking-wide text-muted uppercase">{label}</h3>
                <ul>
                  {ns.map((n) => {
                    const dest = safeDestination(n.action_url);
                    return (
                      <li key={n.id}>
                        <button className={cn('flex w-full flex-col items-start gap-0.5 border-b border-line/60 px-3 py-2 text-left hover:bg-surface-2', !n.read_at && 'bg-brand-tint/30')}
                          onClick={async () => {
                            if (!n.read_at) await markRead([n.id]);
                            refresh();
                            if (dest) { setOpen(false); navigate(dest); }
                          }}>
                          <span className="text-[11px] text-muted">{LABEL[n.category] ?? 'General'} · {label === 'Earlier' ? fmtDate(n.created_at) : fmtTime(n.created_at)}</span>
                          <span className={cn('text-sm text-ink', !n.read_at && 'font-semibold')}>{n.title}</span>
                          {n.body && <span className="text-[12px] text-ink-2">{n.body}</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
